import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SegmentedControl, Select, useLocalizeMessage, } from 'libs.nucleus';
export const EnvironmentSelector = ({ environments, selectedEnvironmentId, onChange, appearance = 'segmented', selectTestId = 'site-configuration-environment-select', }) => {
    const translate = useLocalizeMessage();
    const [deploymentSegment, setDeploymentSegment] = useState('test');
    // A parent-chosen environment can live in the other segment. Follow it until the builder
    // clicks Test or Production, otherwise the first environment in the default segment wins.
    const segmentChosenRef = useRef(false);
    const segmentItems = useMemo(() => [
        { id: 'test', label: translate('Test') },
        { id: 'production', label: translate('Production') },
    ], [translate]);
    const activeSegmentIndex = deploymentSegment === 'test' ? 0 : 1;
    const listsAllEnvironments = appearance === 'organization' || appearance === 'environment';
    const showSegmentedControl = appearance === 'segmented' || appearance === 'segmented-organization';
    const selectWidth = appearance === 'environment' || appearance === 'organization' || appearance === 'segmented-organization'
        ? 'full'
        : 'sm';
    const filteredEnvironments = useMemo(() => {
        const list = environments ?? [];
        if (listsAllEnvironments) {
            return list;
        }
        return list.filter((env) => (deploymentSegment === 'test' ? !env.isProd : env.isProd));
    }, [deploymentSegment, environments, listsAllEnvironments]);
    const environmentOptions = useMemo(() => {
        return filteredEnvironments.map((env) => ({
            value: String(env.id),
            label: env.name,
            meta: {
                environmentId: env.id,
            },
        }));
    }, [filteredEnvironments]);
    const selectedOption = useMemo(() => environmentOptions.find((option) => option.meta?.environmentId === selectedEnvironmentId), [environmentOptions, selectedEnvironmentId]);
    useEffect(() => {
        if (segmentChosenRef.current || listsAllEnvironments) {
            return;
        }
        const selected = (environments ?? []).find((env) => env.id === selectedEnvironmentId);
        if (!selected) {
            return;
        }
        const nextSegment = selected.isProd ? 'production' : 'test';
        setDeploymentSegment((current) => (current === nextSegment ? current : nextSegment));
    }, [environments, listsAllEnvironments, selectedEnvironmentId]);
    useEffect(() => {
        const selected = (environments ?? []).find((env) => env.id === selectedEnvironmentId);
        const waitForSegment = Boolean(selected) && !listsAllEnvironments && !segmentChosenRef.current;
        if (environmentOptions.length === 0) {
            if (selectedEnvironmentId !== undefined && !waitForSegment) {
                onChange(undefined);
            }
            return;
        }
        const isSelectionInFilteredList = environmentOptions.some((option) => option.meta?.environmentId === selectedEnvironmentId);
        if (isSelectionInFilteredList || waitForSegment) {
            return;
        }
        const firstEnvironmentId = environmentOptions[0]?.meta?.environmentId;
        if (firstEnvironmentId !== undefined) {
            onChange(firstEnvironmentId);
        }
    }, [environmentOptions, environments, listsAllEnvironments, onChange, selectedEnvironmentId]);
    const handleSegmentClick = useCallback((item) => {
        segmentChosenRef.current = true;
        setDeploymentSegment(item.id);
    }, []);
    const handleSelectChange = useCallback((option) => {
        const fromMeta = option.meta?.environmentId;
        const fromValue = Number(option.value);
        const id = fromMeta ?? (Number.isFinite(fromValue) ? fromValue : undefined);
        if (id !== undefined) {
            onChange(id);
        }
    }, [onChange]);
    const environmentSelectLabel = appearance === 'organization' || appearance === 'segmented-organization'
        ? translate('Organization')
        : appearance === 'environment'
            ? translate('Environment')
            : translate('Select environment to configure sites of:');
    const environmentSelectPlaceholder = useMemo(() => {
        if (environmentOptions.length > 0) {
            return translate('Choose an environment');
        }
        if (listsAllEnvironments) {
            return translate('No environments available');
        }
        return deploymentSegment === 'test'
            ? translate('No test environments available')
            : translate('No production environments available');
    }, [deploymentSegment, environmentOptions.length, listsAllEnvironments, translate]);
    return (_jsxs("div", { className: 'nucleus-flex nucleus-shrink-0 nucleus-flex-row nucleus-items-end nucleus-gap-4', children: [showSegmentedControl && (_jsx(SegmentedControl, { activeItemIndex: activeSegmentIndex, items: segmentItems, onItemClick: handleSegmentClick })), _jsx("div", { className: appearance === 'environment' || appearance === 'organization' || appearance === 'segmented-organization'
                    ? 'w-[258px]'
                    : undefined, children: _jsx(Select, { "aria-label": environmentSelectLabel, label: listsAllEnvironments || appearance === 'segmented-organization' ? environmentSelectLabel : undefined, placeholder: environmentSelectPlaceholder, options: environmentOptions, value: selectedOption, onChange: handleSelectChange, width: selectWidth, disabled: environmentOptions.length === 0, dataTestId: selectTestId }) })] }));
};
